import React, { useRef, useEffect, useState, useMemo } from "react";
import "../css/topics.scss";
import { get_dontion_list } from "../apis/GetDontionList.js";
import { LIST_URL } from "../constans/ApiEndpointConstants.js";
import { useNavigate } from "react-router-dom";
import Loading1 from "./common/Loading1.js";
import { generateDonationUrl } from "../utils/urlUtils";

const topicData = {
  hero: {
    image: "/main/main3.png",
    tag: "2026 on HeartLink",
    title: "The Year In Help 2026",
    description:
      "This year, our community showed that kindness knows no borders. This is the story of how generosity traveled across the world in 2026.",
    cta: "Read now",
  },
};

const Topics = () => {
  const [loading, setLoading] = useState(true);
  const gridRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [donationsData, setDonationsData] = useState([]);
  const navigate = useNavigate();

  const resolveImageUrl = (raw) => {
    if (!raw) return "";
    if (/^https?:\/\//i.test(raw) || raw.startsWith("data:")) return raw;
    const base = LIST_URL.endsWith("/") ? LIST_URL.slice(0, -1) : LIST_URL;
    return raw.startsWith("/") ? base + raw : base + "/" + raw;
  };

  // fetch donation list once and store
  useEffect(() => {
    let mounted = true;
    const fetchList = async () => {
      try {
        const resp = await get_dontion_list("1", "20000", "US");
        const orders = resp?.data;
        const projectList = orders.projects || [];
        if (mounted) setDonationsData(projectList);
      } catch (err) {
        console.error("Failed to fetch donations for Topics:", err);
      } finally {
        if (mounted) setLoading(false);
      }
    };
    fetchList();
    return () => {
      mounted = false;
    };
  }, []);

  const randomThreeDonations = useMemo(() => {
    if (!Array.isArray(donationsData) || donationsData.length === 0) {
      return [null, null, null];
    }
    const copy = donationsData.slice();
    for (let i = copy.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      const tmp = copy[i];
      copy[i] = copy[j];
      copy[j] = tmp;
    }
    return copy.slice(0, 3);
  }, [donationsData]);

  useEffect(() => {
    // reset active index when items change
    setActiveIndex(0);
  }, [randomThreeDonations]);

  const handleGridScroll = () => {
    const el = gridRef.current;
    if (!el) return;
    const cards = el.querySelectorAll(".topic-card");
    if (!cards || cards.length === 0) return;
    const center = el.scrollLeft + el.clientWidth / 2;
    let closest = 0;
    let best = Infinity;
    cards.forEach((card, i) => {
      const cardCenter = card.offsetLeft + card.clientWidth / 2;
      const d = Math.abs(cardCenter - center);
      if (d < best) {
        best = d;
        closest = i;
      }
    });
    setActiveIndex(closest);
  };

  const handleDotClick = (index) => {
    setActiveIndex(index);
    const el = gridRef.current;
    if (!el) return;
    const cards = el.querySelectorAll(".topic-card");
    const card = cards[index];
    if (!card) return;
    if (el.scrollWidth > el.clientWidth) {
      el.scrollTo({ left: card.offsetLeft, behavior: "smooth" });
    } else {
      card.scrollIntoView({
        behavior: "smooth",
        inline: "center",
        block: "nearest",
      });
    }
  };

  return (
    <>
      <section className="topics">
        <div className="topics__inner">
          <div className="topics__header">
            <h2>Featured topics</h2>
          </div>

          <div className="topics__hero">
            <div className="topics__hero-media">
              <img src={topicData.hero.image} alt={topicData.hero.title} />
              <span className="topics__tag">{topicData.hero.tag}</span>
            </div>
            <div className="topics__hero-body">
              <div>
                <h3 className="topics__hero-title">{topicData.hero.title}</h3>
                <p className="topics__hero-desc">
                  {topicData.hero.description}
                </p>
              </div>
            </div>
          </div>

          {/* 3 random donation cards */}
          <div
            className="topics__grid"
            ref={gridRef}
            onScroll={handleGridScroll}
          >
            {randomThreeDonations.map((d, idx) => {
              if (loading || !d) {
                return (
                  <article className="topic-card topic-card--loading" key={idx}>
                    <div className="topic-card__media placeholder" />
                    <div className="topic-card__body">
                      <h4 className="topic-card__title">
                        <div className="donations">
                          <Loading1 />
                        </div>
                      </h4>
                      <div className="topic-card__cta placeholder"> </div>
                    </div>
                  </article>
                );
              }

              return (
                <article
                  className={`topic-card ${
                    activeIndex === idx ? "topic-card--active" : ""
                  }`}
                  key={d.id || d._id || idx}
                  onClick={() => {
                    setActiveIndex(idx);
                    const url = generateDonationUrl(d);
                    if (url) navigate(url);
                  }}
                >
                  <div className="topic-card__media">
                    <img
                      src={resolveImageUrl(d?.image?.[0]?.url || d?.img || "")}
                      alt={d?.title || "Donation"}
                    />
                    <span className="topics__tag small">
                      {d?.postType || "Other"}
                    </span>
                  </div>
                  <div className="topic-card__body">
                    <h4 className="topic-card__title">
                      {d?.title || "Untitled"}
                    </h4>
                    <button
                      className="topic-card__cta"
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        const url = generateDonationUrl(d);
                        if (url) navigate(url);
                      }}
                    >
                      View <span className="arrow">›</span>
                    </button>
                  </div>
                </article>
              );
            })}
          </div>
          {/* dots showing 3 slots; active dot is black */}
          <div className="topics__count-dots" aria-hidden>
            {Array.from({ length: 3 }).map((_, i) => (
              <button
                key={i}
                type="button"
                className={`topics__count-dot ${
                  i === activeIndex ? "active" : ""
                }`}
                onClick={() => handleDotClick(i)}
                aria-label={`Go to item ${i + 1}`}
              />
            ))}
          </div>
        </div>
      </section>{" "}
      {/* 2 */}
      <div className="topics__info">
        <div className="topics__info-inner">
          <h3 className="topics__info-title">
            Fundraising on HeartLink is easy, powerful, and trusted.
          </h3>
          <p className="topics__info-desc">
            Get what you need to help your fundraiser succeed on HeartLink,
            whether you’re raising money for yourself, friends, family, or
            charity. With no fee to start, HeartLink is the world’s leading
            crowdfunding platform—from memorial tributes and funerals to medical
            emergencies and nonprofits . Whenever you need help, you can ask
            here.
          </p>
        </div>
      </div>
      {/* 3 */}
    </>
  );
};

export default Topics;
